import { useEffect, useState } from "react";
import { useTheme } from "../contexts/ThemeContext";
import {
  listarGuias,
  mudarStatus,
  softDelete,
  restaurar,
  STATUS_GUIA,
} from "../services/adminService";
import AdminMetricasPanel from "../components/admin/AdminMetricasPanel";
import ConfirmDialog from "../components/ui/ConfirmDialog";
import Loading, { ErrorBox } from "../components/ui/Loading";

const PAGE_SIZE = 20;

// Layout em grid pra alinhar header e linhas: Email · Nome · Empresa · Status ·
// Cadastrado em · Ações.
const GRID =
  "minmax(190px,1.6fr) minmax(120px,1.1fr) minmax(110px,1fr) 120px 120px 170px";

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("pt-BR");
}

export default function AdminGuiasPage() {
  const { C } = useTheme();

  // ── Filtros ──
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [status, setStatus] = useState(""); // "" = todos
  const [incluirDeletados, setIncluirDeletados] = useState(false);
  const [page, setPage] = useState(1);

  // ── Dados ──
  const [result, setResult] = useState(null); // PagedResultDto
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ── Re-fetch coordenado (lista + métricas) após ações ──
  const [reloadFlag, setReloadFlag] = useState(0);
  const [metricsKey, setMetricsKey] = useState(0);

  // ── Banner inline (sucesso/erro) com auto-dismiss ──
  const [banner, setBanner] = useState(null); // { type: "success"|"error", text }

  // ── Confirmação de ações destrutivas ──
  const [confirm, setConfirm] = useState(null); // { title, message, onConfirm }
  const [confirmLoading, setConfirmLoading] = useState(false);

  // Debounce da busca: 350ms após parar de digitar. Reseta pra página 1
  // sempre que o termo muda (resultado novo começa do topo).
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedQ(q);
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [q]);

  // Fetch principal — reage a filtros, página e reloadFlag (pós-ação).
  useEffect(() => {
    let active = true;
    setLoading(true);
    listarGuias({ q: debouncedQ, status, incluirDeletados, page, pageSize: PAGE_SIZE })
      .then((data) => {
        if (active) {
          setResult(data);
          setError(null);
        }
      })
      .catch(() => active && setError("Não foi possível carregar os guias."))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [debouncedQ, status, incluirDeletados, page, reloadFlag]);

  // Auto-dismiss do banner em ~4s.
  useEffect(() => {
    if (!banner) return;
    const t = setTimeout(() => setBanner(null), 4000);
    return () => clearTimeout(t);
  }, [banner]);

  function bumpReload() {
    setReloadFlag((n) => n + 1);
    setMetricsKey((n) => n + 1);
  }

  // Ação direta (sem confirmação): Reativar / Restaurar.
  async function runDirect(actionFn, successMsg) {
    try {
      await actionFn();
      setBanner({ type: "success", text: successMsg });
      bumpReload();
    } catch (err) {
      setBanner({ type: "error", text: err.message || "Falha na operação." });
    }
  }

  // Ação confirmada (destrutiva): Suspender / Trial / Soft-delete.
  function askConfirm({ title, message, actionFn, successMsg }) {
    setConfirm({
      title,
      message,
      onConfirm: async () => {
        setConfirmLoading(true);
        try {
          await actionFn();
          setConfirm(null);
          setBanner({ type: "success", text: successMsg });
          bumpReload();
        } catch (err) {
          setConfirm(null);
          setBanner({ type: "error", text: err.message || "Falha na operação." });
        } finally {
          setConfirmLoading(false);
        }
      },
    });
  }

  const items = result?.items ?? [];
  const totalItems = result?.totalItems ?? 0;
  const totalPages = result?.totalPages ?? 0;

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: C.text, margin: 0 }}>
          🛠️ Admin TecmovelTurismo
        </h1>
        <p style={{ color: C.textMuted, fontSize: 13, margin: "4px 0 0" }}>
          Administração de guias do SaaS — {totalItems} guia{totalItems === 1 ? "" : "s"}
          {status === "" && !debouncedQ && !incluirDeletados ? " no total" : " no filtro atual"}
        </p>
      </div>

      {/* Banner inline */}
      {banner && (
        <div
          style={{
            marginBottom: 18,
            padding: "12px 16px",
            borderRadius: 10,
            fontSize: 13,
            fontWeight: 600,
            background: banner.type === "success" ? C.greenDim : C.redDim,
            color: banner.type === "success" ? C.green : C.red,
            border: `1px solid ${banner.type === "success" ? C.green : C.red}33`,
          }}
        >
          {banner.text}
        </div>
      )}

      {/* Métricas */}
      <AdminMetricasPanel refreshKey={metricsKey} />

      {/* Barra de filtros */}
      <div
        style={{
          display: "flex",
          gap: 12,
          flexWrap: "wrap",
          alignItems: "center",
          marginBottom: 18,
        }}
      >
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar por e-mail, nome ou empresa..."
          style={{
            flex: 1,
            minWidth: 240,
            maxWidth: 360,
            padding: "10px 16px",
            borderRadius: 10,
            border: `1px solid ${C.border}`,
            background: C.inputBg,
            color: C.text,
            fontSize: 13,
            outline: "none",
            fontFamily: "'DM Sans', sans-serif",
            boxSizing: "border-box",
          }}
        />
        <select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
          style={{
            padding: "10px 14px",
            borderRadius: 10,
            border: `1px solid ${C.border}`,
            background: C.inputBg,
            color: C.text,
            fontSize: 13,
            outline: "none",
            fontFamily: "'DM Sans', sans-serif",
            cursor: "pointer",
          }}
        >
          <option value="">Todos os status</option>
          <option value={STATUS_GUIA.Ativo}>Ativo</option>
          <option value={STATUS_GUIA.Suspenso}>Suspenso</option>
          <option value={STATUS_GUIA.TrialExpirado}>Trial expirado</option>
        </select>
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: 13,
            color: C.textMuted,
            cursor: "pointer",
            userSelect: "none",
          }}
        >
          <input
            type="checkbox"
            checked={incluirDeletados}
            onChange={(e) => {
              setIncluirDeletados(e.target.checked);
              setPage(1);
            }}
            style={{ cursor: "pointer" }}
          />
          Incluir deletados
        </label>
      </div>

      {loading && <Loading />}
      {error && <ErrorBox message={error} />}

      {!loading && !error && (
        <div
          style={{
            background: C.bgCard,
            borderRadius: 12,
            border: `1px solid ${C.border}`,
            overflow: "hidden",
          }}
        >
          {/* Header */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: GRID,
              gap: 12,
              padding: "12px 18px",
              borderBottom: `1px solid ${C.border}`,
              fontSize: 11,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: 0.5,
              color: C.textMuted,
            }}
          >
            <span>E-mail</span>
            <span>Nome</span>
            <span>Empresa</span>
            <span>Status</span>
            <span>Cadastrado</span>
            <span style={{ textAlign: "right" }}>Ações</span>
          </div>

          {/* Linhas */}
          {items.map((g) => (
            <GuiaRow
              key={g.id}
              g={g}
              C={C}
              onSuspender={() =>
                askConfirm({
                  title: "Suspender guia",
                  message: `Suspender "${g.nome}"? O guia não conseguirá logar até a reativação.`,
                  actionFn: () => mudarStatus(g.id, STATUS_GUIA.Suspenso),
                  successMsg: "Guia suspenso.",
                })
              }
              onTrial={() =>
                askConfirm({
                  title: "Marcar trial expirado",
                  message: `Marcar "${g.nome}" como trial expirado? O acesso fica bloqueado até a reativação.`,
                  actionFn: () => mudarStatus(g.id, STATUS_GUIA.TrialExpirado),
                  successMsg: "Marcado como trial expirado.",
                })
              }
              onSoftDelete={() =>
                askConfirm({
                  title: "Excluir guia",
                  message: `Excluir "${g.nome}"? A conta será desativada (soft-delete) e pode ser restaurada depois.`,
                  actionFn: () => softDelete(g.id),
                  successMsg: "Guia excluído.",
                })
              }
              onReativar={() =>
                runDirect(() => mudarStatus(g.id, STATUS_GUIA.Ativo), "Guia reativado.")
              }
              onRestaurar={() => runDirect(() => restaurar(g.id), "Guia restaurado.")}
            />
          ))}

          {items.length === 0 && (
            <div style={{ fontSize: 13, color: C.textMuted, padding: 24, textAlign: "center" }}>
              Nenhum guia encontrado com os filtros atuais.
            </div>
          )}
        </div>
      )}

      {/* Paginação */}
      {!loading && !error && totalPages > 1 && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 16,
            marginTop: 20,
          }}
        >
          <PagerButton C={C} disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            ‹ Anterior
          </PagerButton>
          <span style={{ fontSize: 13, color: C.textMuted }}>
            Página {page} de {totalPages}
          </span>
          <PagerButton
            C={C}
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Próxima ›
          </PagerButton>
        </div>
      )}

      <ConfirmDialog
        open={!!confirm}
        title={confirm?.title}
        message={confirm?.message}
        confirmLabel="Confirmar"
        danger
        loading={confirmLoading}
        onCancel={() => (confirmLoading ? undefined : setConfirm(null))}
        onConfirm={confirm?.onConfirm}
      />
    </div>
  );
}

// ── Linha da tabela ──
function GuiaRow({ g, C, onSuspender, onTrial, onSoftDelete, onReativar, onRestaurar }) {
  const deletado = g.deletadoEm != null;

  const STATUS_STYLE = {
    [STATUS_GUIA.Ativo]: { label: "Ativo", bg: C.greenDim, color: C.green },
    [STATUS_GUIA.Suspenso]: { label: "Suspenso", bg: C.redDim, color: C.red },
    [STATUS_GUIA.TrialExpirado]: { label: "Trial expirado", bg: C.bgCardHover, color: C.textMuted },
  };
  const st = STATUS_STYLE[g.status] || { label: String(g.status), bg: C.bgCard, color: C.textMuted };

  // Owner é intocável: todas as ações desabilitadas com tooltip explicativo.
  const ownerTip = "Você não pode modificar a própria conta de Owner.";
  const disabled = g.isOwner;

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: GRID,
        gap: 12,
        padding: "14px 18px",
        borderTop: `1px solid ${C.border}`,
        alignItems: "center",
        fontSize: 13,
        opacity: deletado ? 0.55 : 1,
      }}
    >
      <span
        style={{
          color: C.text,
          fontWeight: 600,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          textDecoration: deletado ? "line-through" : "none",
        }}
        title={g.email}
      >
        {g.email || "—"}
        {g.isOwner && (
          <span
            style={{
              marginLeft: 6,
              fontSize: 10,
              fontWeight: 700,
              color: C.accent,
              background: C.accentDim,
              padding: "2px 6px",
              borderRadius: 5,
            }}
          >
            OWNER
          </span>
        )}
      </span>
      <span style={{ color: C.textMuted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {g.nome || "—"}
      </span>
      <span style={{ color: C.textMuted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {g.empresa || "—"}
      </span>
      <span>
        {deletado ? (
          <span
            style={{
              background: C.bgCard,
              color: C.textMuted,
              padding: "6px 9px",
              borderRadius: 6,
              fontSize: 11,
              fontWeight: 600,
              border: `1px solid ${C.border}`,
            }}
          >
            Deletado
          </span>
        ) : (
          <span
            style={{
              background: st.bg,
              color: st.color,
              padding: "6px 9px",
              borderRadius: 6,
              fontSize: 11,
              fontWeight: 600,
              whiteSpace: "nowrap",
            }}
          >
            {st.label}
          </span>
        )}
      </span>
      <span style={{ color: C.textMuted }}>{formatDate(g.criadoEm)}</span>

      {/* Ações */}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 6 }}>
        {deletado ? (
          <IconBtn C={C} disabled={disabled} title={disabled ? ownerTip : "Restaurar"} onClick={onRestaurar}>
            ↻
          </IconBtn>
        ) : g.status === STATUS_GUIA.Ativo ? (
          <>
            <IconBtn C={C} disabled={disabled} title={disabled ? ownerTip : "Suspender"} onClick={onSuspender}>
              ⏸
            </IconBtn>
            <IconBtn C={C} disabled={disabled} title={disabled ? ownerTip : "Marcar trial expirado"} onClick={onTrial}>
              ⏳
            </IconBtn>
            <IconBtn C={C} disabled={disabled} danger title={disabled ? ownerTip : "Excluir (soft-delete)"} onClick={onSoftDelete}>
              🗑
            </IconBtn>
          </>
        ) : (
          <>
            <IconBtn C={C} disabled={disabled} title={disabled ? ownerTip : "Reativar"} onClick={onReativar}>
              ▶
            </IconBtn>
            <IconBtn C={C} disabled={disabled} danger title={disabled ? ownerTip : "Excluir (soft-delete)"} onClick={onSoftDelete}>
              🗑
            </IconBtn>
          </>
        )}
      </div>
    </div>
  );
}

// Botão de ação por linha — icon + tooltip via title nativo.
function IconBtn({ C, children, onClick, disabled, danger, title }) {
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      title={title}
      style={{
        width: 32,
        height: 32,
        borderRadius: 8,
        border: `1px solid ${C.border}`,
        background: "transparent",
        color: danger ? C.red : C.textMuted,
        fontSize: 14,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.4 : 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        lineHeight: 1,
      }}
    >
      {children}
    </button>
  );
}

function PagerButton({ C, children, onClick, disabled }) {
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      style={{
        padding: "8px 16px",
        borderRadius: 10,
        border: `1px solid ${C.border}`,
        background: "transparent",
        color: disabled ? C.textDim : C.text,
        fontSize: 13,
        fontWeight: 600,
        cursor: disabled ? "not-allowed" : "pointer",
        fontFamily: "'DM Sans', sans-serif",
      }}
    >
      {children}
    </button>
  );
}
